import * as turf from '@turf/turf';
import type { Feature, GeoJsonProperties, Polygon as GJPolygon } from 'geojson';
import { CIRCLE_STEPS, MAX_CIRCLE_HA, MIN_CIRCLE_HA } from './map-editor.constants';
import { orientedFeature } from './map-geometry.utils';
import { pointAreaHa, withPointArea } from './map-point-editor.component';

export function haToRadiusKm(ha: number): number {
  const radiusMeters = Math.sqrt((ha * 10000) / Math.PI);
  return radiusMeters / 1000;
}

export function buildCircleFeature(
  lng: number,
  lat: number,
  radiusHa: number,
  extraProps?: GeoJsonProperties
): Feature<GJPolygon> {
  const clampedHa = Math.max(MIN_CIRCLE_HA, Math.min(radiusHa, MAX_CIRCLE_HA));
  const circle = orientedFeature(
    turf.circle([lng, lat], haToRadiusKm(clampedHa), { steps: CIRCLE_STEPS, units: 'kilometers' }) as any
  );
  (circle.properties as any) = {
    ...(extraProps ?? {}),
    ...(circle.properties ?? {}),
    _circle: true,
    shape: 'circle',
    centerLng: lng,
    centerLat: lat,
    radiusHa: clampedHa,
  };
  return circle;
}

export function pointFeatureAt(coords: any, props?: GeoJsonProperties, id?: string | number | null): Feature | null {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [lng, lat] = coords;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return {
    type: 'Feature',
    ...(id != null ? { id: String(id) } : {}),
    properties: withPointArea(props, pointAreaHa(props)),
    geometry: { type: 'Point', coordinates: [lng, lat] },
  };
}

function presentPoints(points: (Feature | null)[]): Feature[] {
  return points.filter((point): point is Feature => point !== null);
}

function convertFeaturePoints(feature: any): Feature[] {
  if (!feature) return [];
  const geom = feature.geometry;
  if (!geom) return [feature];
  if (geom.type === 'Point') return presentPoints([pointFeatureAt(geom.coordinates, feature.properties, feature.id)]);
  if (geom.type === 'MultiPoint') {
    const coords = Array.isArray(geom.coordinates) ? geom.coordinates : [];
    return presentPoints(
      coords.map((coord: any, idx: number) =>
        pointFeatureAt(coord, feature.properties, feature.id != null ? `${feature.id}-${idx}` : undefined)
      )
    );
  }
  return [feature];
}

function singleOrCollection(features: Feature[], fallback: any): any {
  if (features.length === 1) return features[0];
  if (features.length > 1) return { type: 'FeatureCollection', features };
  return fallback;
}

export function convertPointFeatures(input: any): any {
  if (!input) return input;
  if (input.type === 'FeatureCollection') {
    return { ...input, features: (input.features ?? []).flatMap((feat: any) => convertFeaturePoints(feat)) };
  }
  if (input.type === 'Feature') return singleOrCollection(convertFeaturePoints(input), input);
  if (input.type === 'Point') return pointFeatureAt(input.coordinates) ?? input;
  if (input.type === 'MultiPoint') {
    const coords = Array.isArray(input.coordinates) ? input.coordinates : [];
    return singleOrCollection(
      presentPoints(coords.map((coord: any, idx: number) => pointFeatureAt(coord, undefined, idx))),
      input
    );
  }
  return input;
}
